import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, Textarea, ErrorSummary } from 'nhsuk-react-components';
import { listOrganisations, createOrganisation, updateOrganisation, deleteOrganisation, getOrganisationDetails } from '../../actions/organisationActions';
import { ORGANISATION_CREATE_RESET, ORGANISATION_UPDATE_RESET } from '../../constants/organisationConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

const emptyOrganisation = {
    name: '',
    email: '',
    telephone: '',
    address: '',
    postalCode: '',
    city: '',
    state: '',
    country: '',
    website: '',
    description: '',
    odsCode: '',
};

const organisationPayload = (org) => ({
    name: org.name || '',
    email: org.email || '',
    telephone: org.telephone || '',
    address: org.address || '',
    postalCode: org.postalCode || '',
    city: org.city || '',
    state: org.state || '',
    country: org.country || '',
    website: org.website || '',
    description: org.description || '',
    fhirServerUrl: org.fhirServerUrl || '',
    fhirServerVersion: org.fhirServerVersion || '',
    identifier: org.identifier || '',
    odsCode: org.odsCode || '',
});

export default function OrganisationCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Organisations";
    }, []);

    useEffect(() => {
        dispatch({ type: ORGANISATION_CREATE_RESET });
        dispatch({ type: ORGANISATION_UPDATE_RESET });
    }, [dispatch]);

    const [organisationListFilter, setorganisationListFilter] = useState({ query: '', page: 1, filter: 'Name' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newOrganisation, setNewOrganisation] = useState({ ...emptyOrganisation });
    const [updatedOrganisation, setUpdatedOrganisation] = useState({});

    const organisationList = useSelector((state) => state.organisationList);
    const { loading, error, organisations, currentPage, totalPages, totalCount } = organisationList;

    const organisationCreate = useSelector((state) => state.organisationCreate);
    const { success: successCreate, error: organisationCreateErrors, loading: organisationCreateLoading } = organisationCreate;

    const organisationUpdate = useSelector((state) => state.organisationUpdate);
    const { success: successUpdate, error: organisationUpdateErrors, loading: organisationUpdateProcessing } = organisationUpdate;

    const organisationDetails = useSelector((state) => state.organisationDetails);
    const { organisation: organisationDet, loading: organisationDetailsLoading } = organisationDetails;

    useEffect(() => {
        dispatch(listOrganisations(organisationListFilter));
    }, [dispatch, organisationListFilter]);

    useEffect(() => {
        if (organisationDet && organisationDet.id) {
            setUpdatedOrganisation({ ...organisationDet });
        }
    }, [organisationDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewOrganisation({ ...emptyOrganisation });
            dispatch(listOrganisations(organisationListFilter));
            dispatch({ type: ORGANISATION_CREATE_RESET });
        }
    }, [successCreate, dispatch, organisationListFilter]);

    const handlePageChange = (newPage) => {
        setorganisationListFilter({ ...organisationListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setorganisationListFilter({ ...organisationListFilter, query: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this organisation?')) {
            dispatch(deleteOrganisation(id)).then(() => {
                dispatch(listOrganisations(organisationListFilter));
                if (updatedOrganisation?.id === id) {
                    setShowEditModal(false);
                    setUpdatedOrganisation({});
                }
            });
        }
    };

    const handleEditOrganisation = (id) => {
        dispatch(getOrganisationDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedOrganisation({});
        dispatch({ type: ORGANISATION_UPDATE_RESET });
    };

    const handleCreateOrganisation = () => {
        dispatch(createOrganisation(organisationPayload(newOrganisation)));
    };

    const handleUpdateOrganisation = () => {
        dispatch(updateOrganisation(updatedOrganisation.id, organisationPayload(updatedOrganisation))).then(() => {
            dispatch(listOrganisations(organisationListFilter));
        });
    };

    const openCreateModal = () => {
        setNewOrganisation({ ...emptyOrganisation });
        dispatch({ type: ORGANISATION_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    const errorList = (errors) => (
        Array.isArray(errors) ? errors : errors ? [String(errors)] : []
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="Organisation Management" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4"
                                    onClick={openCreateModal}
                                >
                                    + Create Organisation
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-organisations"
                                        label="Search organisations"
                                        name="search-organisations"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {organisations && organisations.length > 0 ? (
                                            organisations.map((organisation) => (
                                                <Details key={organisation.id}>
                                                    <Details.Summary>
                                                        {organisation.name}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Address:</strong> {organisation.address}
                                                        </p>
                                                        <p>
                                                            <strong>City:</strong> {organisation.city}
                                                        </p>
                                                        <p>
                                                            <strong>Email:</strong> {organisation.email}
                                                        </p>
                                                        <p>
                                                            <strong>Phone:</strong> {organisation.telephone || '-'}
                                                        </p>
                                                        <p>
                                                            <strong>ODS code:</strong> {organisation.odsCode || 'Not set'}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={organisationDetailsLoading}
                                                                onClick={() => handleEditOrganisation(organisation.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(organisation.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: ORGANISATION_CREATE_RESET });
                }}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Create Organisation</h2>
                {errorList(organisationCreateErrors).length > 0 && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {errorList(organisationCreateErrors).map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input id="name-create" label="Name" name="name-create" type="text" required value={newOrganisation.name} onChange={(e) => setNewOrganisation({ ...newOrganisation, name: e.target.value })} />
                <Input id="odsCode-create" label="ODS code" name="odsCode-create" type="text" hint="NHS organisation code, for example R1H. Sign-in matches on this." value={newOrganisation.odsCode} onChange={(e) => setNewOrganisation({ ...newOrganisation, odsCode: e.target.value })} />
                <Input id="email-create" label="Email" name="email-create" type="email" required value={newOrganisation.email} onChange={(e) => setNewOrganisation({ ...newOrganisation, email: e.target.value })} />
                <Input id="telephone-create" label="Telephone" name="telephone-create" type="text" required value={newOrganisation.telephone} onChange={(e) => setNewOrganisation({ ...newOrganisation, telephone: e.target.value })} />
                <Input id="address-create" label="Address" name="address-create" type="text" required value={newOrganisation.address} onChange={(e) => setNewOrganisation({ ...newOrganisation, address: e.target.value })} />
                <Input id="postalCode-create" label="Postal Code" name="postalCode-create" type="text" value={newOrganisation.postalCode} onChange={(e) => setNewOrganisation({ ...newOrganisation, postalCode: e.target.value })} />
                <Input id="city-create" label="City" name="city-create" type="text" required value={newOrganisation.city} onChange={(e) => setNewOrganisation({ ...newOrganisation, city: e.target.value })} />
                <Input id="state-create" label="State" name="state-create" type="text" value={newOrganisation.state} onChange={(e) => setNewOrganisation({ ...newOrganisation, state: e.target.value })} />
                <Input id="country-create" label="Country" name="country-create" type="text" value={newOrganisation.country} onChange={(e) => setNewOrganisation({ ...newOrganisation, country: e.target.value })} />
                <Input id="website-create" label="Website" name="website-create" type="text" value={newOrganisation.website} onChange={(e) => setNewOrganisation({ ...newOrganisation, website: e.target.value })} />
                <Textarea id="description-create" label="Description" name="description-create" value={newOrganisation.description} onChange={(e) => setNewOrganisation({ ...newOrganisation, description: e.target.value })} />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button className="nhsuk-button--small" disabled={organisationCreateLoading} onClick={handleCreateOrganisation}>
                        Create Organisation
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: ORGANISATION_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Edit Organisation</h2>
                {organisationDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        {errorList(organisationUpdateErrors).length > 0 && (
                            <ErrorSummary aria-labelledby="org-update-error" role="alert" tabIndex="-1">
                                <ErrorSummary.Title id="org-update-error">There is a problem</ErrorSummary.Title>
                                <ErrorSummary.Body>
                                    <ul className="nhsuk-list nhsuk-list--bullet">
                                        {errorList(organisationUpdateErrors).map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </ul>
                                </ErrorSummary.Body>
                            </ErrorSummary>
                        )}
                        <Input id="name" label="Name" name="name" type="text" required value={updatedOrganisation.name || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, name: e.target.value })} />
                        <Input id="odsCode" label="ODS code" name="odsCode" type="text" hint="NHS organisation code, for example R1H. Sign-in matches on this." value={updatedOrganisation.odsCode || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, odsCode: e.target.value })} />
                        <Input id="email" label="Email" name="email" type="email" required value={updatedOrganisation.email || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, email: e.target.value })} />
                        <Input id="telephone" label="Telephone" name="telephone" type="text" required value={updatedOrganisation.telephone || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, telephone: e.target.value })} />
                        <Input id="address" label="Address" name="address" type="text" required value={updatedOrganisation.address || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, address: e.target.value })} />
                        <Input id="postalCode" label="Postal Code" name="postalCode" type="text" value={updatedOrganisation.postalCode || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, postalCode: e.target.value })} />
                        <Input id="city" label="City" name="city" type="text" required value={updatedOrganisation.city || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, city: e.target.value })} />
                        <Input id="state" label="State" name="state" type="text" value={updatedOrganisation.state || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, state: e.target.value })} />
                        <Input id="country" label="Country" name="country" type="text" value={updatedOrganisation.country || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, country: e.target.value })} />
                        <Input id="website" label="Website" name="website" type="text" value={updatedOrganisation.website || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, website: e.target.value })} />
                        <Textarea id="description" label="Description" name="description" value={updatedOrganisation.description || ''} onChange={(e) => setUpdatedOrganisation({ ...updatedOrganisation, description: e.target.value })} />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The organisation was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button className="nhsuk-button--small" disabled={organisationUpdateProcessing} onClick={handleUpdateOrganisation}>
                                Save changes
                            </Button>
                            <Button secondary className="nhsuk-button--small" onClick={handleCloseEdit}>
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
